/**
 * Tailwind CSS implementation of EmptyState component
 * Uses Seeds empty-state CSS classes from empty-state.css
 */

import React from "react";

import type { TypeEmptyStateProps } from "./EmptyStateTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const EmptyStateTailwind = React.forwardRef<
  HTMLDivElement,
  TypeEmptyStateProps
>(
  (
    {
      media,
      headline,
      subtext,
      primaryAction,
      secondaryAction,
      className,
      ...rest
    },
    ref
  ) => {
    const hasAction = Boolean(primaryAction || secondaryAction);

    return (
      <div
        data-qa-emptystate={headline}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        ref={ref}
        className={cn("seeds-empty-state", className)}
      >
        {media && <div className="seeds-empty-state-media">{media}</div>}

        <div className="seeds-empty-state-content">
          <p className="seeds-empty-state-headline">{headline}</p>

          {subtext && <p className="seeds-empty-state-subtext">{subtext}</p>}

          <div
            className={cn("seeds-empty-state-actions", {
              "seeds-empty-state-actions-empty": !hasAction,
            })}
          >
            {primaryAction &&
              React.cloneElement(primaryAction, {
                appearance: "primary",
              })}

            {secondaryAction && (
              <div className="seeds-empty-state-secondary-action">
                {React.cloneElement(secondaryAction, {
                  appearance: "unstyled",
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

EmptyStateTailwind.displayName = "EmptyStateTailwind";
